import axios from 'axios';
import type { OpenAIResponse, PromptMessage } from './types';
import { getApiKey } from '@/utils/apiKeys';
import { User } from '@/types/common';
import { ApiKeyService } from '@/hooks/useApiKeys';

const CHAT_COMPLETIONS_API_URL = 'https://api.openai.com/v1/chat/completions';
const RESPONSES_API_URL = 'https://api.openai.com/v1/responses';
const LEGACY_CHAT_COMPLETION_MODELS = new Set([
  'gpt-3.5-turbo',
  'gpt-3.5-turbo-0125',
  'gpt-4-turbo-preview',
]);

type ResponsesApiContent = {
  type?: string;
  text?: string;
};

type ResponsesApiOutput = {
  type?: string;
  role?: string;
  content?: ResponsesApiContent[];
};

type ResponsesApiResponse = {
  id?: string;
  created_at?: number;
  model?: string;
  output_text?: string;
  output?: ResponsesApiOutput[];
  usage?: {
    input_tokens?: number;
    output_tokens?: number;
    total_tokens?: number;
  };
};

function getResponsesText(data: ResponsesApiResponse): string {
  if (data.output_text) {
    return data.output_text;
  }

  return data.output
    ?.flatMap(item => item.content ?? [])
    .map(content => content.text ?? '')
    .join('')
    .trim() ?? '';
}

function toResponsesInput(messages: PromptMessage[]) {
  return messages.map(message => ({
    role: message.role === 'system' ? 'developer' : message.role,
    content: message.content,
  }));
}

function normalizeResponsesApiResponse(data: ResponsesApiResponse, model: string): OpenAIResponse {
  const content = getResponsesText(data);

  return {
    id: data.id ?? `response-${Date.now()}`,
    object: 'chat.completion',
    created: data.created_at ?? Math.floor(Date.now() / 1000),
    model: data.model ?? model,
    usage: data.usage ? {
      prompt_tokens: data.usage.input_tokens,
      completion_tokens: data.usage.output_tokens,
      total_tokens: data.usage.total_tokens,
      input_tokens: data.usage.input_tokens,
      output_tokens: data.usage.output_tokens,
    } : undefined,
    choices: [{
      index: 0,
      message: {
        role: 'assistant',
        content,
        refusal: null,
      },
      logprobs: null,
      finish_reason: 'stop',
    }],
  };
}

function supportsReasoning(model: string): boolean {
  return model.startsWith('gpt-6') || model.startsWith('gpt-5') || /^o\d/.test(model);
}

export async function fetchChatGPTAnswer(
  messages: PromptMessage[],
  model: string,
  user: User | null
): Promise<OpenAIResponse> {
  if (!user) {
    throw new Error('User not authenticated');
  }

  const OPENAI_API_KEY = getApiKey(ApiKeyService.OPENAI, user.id);

  if (!OPENAI_API_KEY) {
    throw new Error('OpenAI API key not configured');
  }

  try {
    if (!LEGACY_CHAT_COMPLETION_MODELS.has(model)) {
      const payload = {
        model,
        input: toResponsesInput(messages),
        max_output_tokens: 1000,
        ...(supportsReasoning(model) ? { reasoning: { effort: 'low' } } : {}),
      };

      const response = await axios.post(
        RESPONSES_API_URL,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${OPENAI_API_KEY}`,
            'Content-Type': 'application/json',
          }
        }
      );

      return normalizeResponsesApiResponse(response.data, model);
    }

    // Send the entire message history to maintain conversation context
    const response = await axios.post(
      CHAT_COMPLETIONS_API_URL,
      {
        model: model,
        messages: messages,
        temperature: 0.7,
        max_tokens: 1000,
      },
      {
        headers: {
          'Authorization': `Bearer ${OPENAI_API_KEY}`,
          'Content-Type': 'application/json',
        }
      }
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('ChatGPT API Error:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      throw new Error(`ChatGPT API Error: ${error.response?.data?.error?.message || error.message}`);
    }
    throw error;
  }
}
